import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { addDays, addWeeks, format, isToday, startOfWeek } from 'date-fns'
import { de } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Pencil, Plus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useMenusWeek } from '../hooks/use-menus'
import { CreateDailyDialog } from './create-daily-dialog'
import { MenuDeleteMenu } from './delete-menu-dialog'
import type { Menu } from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

function toISO(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

export function WeekView({ restaurantId }: { restaurantId: number }) {
  const { t: tMenus } = useTranslation('menus')
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canCreate = can(ACTIONS.MENU_CREATE)

  const [anchor, setAnchor] = useState(() => new Date())
  const [createDate, setCreateDate] = useState<string | null>(null)

  const weekStart = startOfWeek(anchor, { weekStartsOn: 1 })
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const from = toISO(days[0])
  const to = toISO(days[6])

  const { data } = useMenusWeek(restaurantId, from, to)
  const byDate = new Map<string, Menu>()
  for (const m of data?.items ?? []) {
    if (m.menuDate) byDate.set(m.menuDate, m)
  }

  const openEditor = (menuId: number) =>
    navigate({ to: '/menus/$menuId', params: { menuId: String(menuId) } })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold">
          {format(days[0], 'dd.MM.', { locale: de })} –{' '}
          {format(days[6], 'dd.MM.yyyy', { locale: de })}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAnchor(addWeeks(anchor, -1))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAnchor(new Date())}
          >
            {tMenus('week.today')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAnchor(addWeeks(anchor, 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
        {days.map((day) => {
          const iso = toISO(day)
          const menu = byDate.get(iso)
          return (
            <Card
              key={iso}
              className={isToday(day) ? 'border-primary' : undefined}
            >
              <CardHeader className="pb-2">
                <div className="text-xs uppercase text-muted-foreground">
                  {format(day, 'EEE', { locale: de })}
                </div>
                <div className="text-lg font-semibold">
                  {format(day, 'dd.MM.', { locale: de })}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {menu ? (
                  <>
                    <Badge
                      variant={
                        menu.status === 'published' ? 'default' : 'secondary'
                      }
                    >
                      {menu.status === 'published'
                        ? tMenus('week.published')
                        : tMenus('week.draft')}
                    </Badge>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditor(menu.id)}
                      >
                        <Pencil className="w-3 h-3 mr-2" />
                        {tMenus('week.edit')}
                      </Button>
                      <MenuDeleteMenu menu={menu} />
                    </div>
                  </>
                ) : (
                  <>
                    <Badge variant="outline">{tMenus('week.none')}</Badge>
                    {canCreate && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setCreateDate(iso)}
                      >
                        <Plus className="w-3 h-3 mr-2" />
                        {tMenus('week.create')}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {createDate && (
        <CreateDailyDialog
          open
          onOpenChange={(o) => {
            if (!o) setCreateDate(null)
          }}
          restaurantId={restaurantId}
          targetDate={createDate}
        />
      )}
    </div>
  )
}
